<template>
  <div class="event-item" data-t="event-item" :data-event-id="event.id">
    <div class="left">
      <CompetitorNames :event="event" />
      <div class="bottom-left">
        <EventStatusBar :event="event" />
        <EventMarketsCount
          :count="event.activeMarketsCount"
          :link="event.url"
        />
      </div>
    </div>
    <div class="right">
      <ScoreBoardMini
        v-if="isLive"
        :event="event"
        size="m"
        class="event-item-scores"
      />

      <div class="lines-wrapper">
        <!-- main line -->
        <EventMarketsLine
          :event="event"
          :markets-filters="mainLine"
          :label="t('markets.match')"
          :is-live="isLive"
          data-t="main-line"
        />
        <!-- period line -->
        <EventMarketsLine
          v-if="isPeriodLineActive"
          :event="event"
          :markets-filters="periodLine?.filteredByPeriod"
          :label="periodLineLabel"
          :is-live="isLive"
          data-t="period-line"
        />
      </div>
    </div>
    <div class="additional-buttons">
      <EventStatsButton
        v-if="isEventStatsVisible"
        :event-id="event.id"
        type="text-tertiary"
        class="event-stats"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TSportEvent } from 'markets-store'
import type { FilterConfig } from 'markets-store/marketsbook'
import { SPORT_EVENT_STATUSES_ENUM } from 'markets-store/constants'
import CompetitorNames from '../CompetitorNames/CompetitorNames.vue'
import EventStatusBar from '../EventStatusBar/EventStatusBar.vue'
import EventMarketsCount from '../EventMarketsCount/EventMarketsCount.vue'
import EventMarketsLine from '../EventMarketsLine/EventMarketsLine.vue'
import ScoreBoardMini from '../ScoreBoardMini/ScoreBoardMini.vue'
import EventStatsButton from '../EventStatsButton/EventStatsButton.vue'
import { usePeriodMarkets, useEventStatsVisibility } from '../../composables'

const props = defineProps<{
  event: TSportEvent
  marketsFilters?: (FilterConfig | null)[]
}>()

const { t } = useI18n()

const { event, marketsFilters } = toRefs(props)

const { mainLine, periodLine, periodLineLabel, isPeriodLineActive } =
  usePeriodMarkets(event, marketsFilters)

const isLive = computed(
  () => event.value.status === SPORT_EVENT_STATUSES_ENUM.live,
)

const { isEventStatsVisible } = useEventStatsVisibility()
</script>

<style scoped>
.event-item {
  display: flex;
  gap: var(--spacing-150);
  padding: 0 var(--spacing-050) 0 var(--spacing-100);
  border-bottom: 1px solid rgb(255 255 255 / 8%);

  &:last-child {
    border: none;
  }
}

.event-item-markets {
  display: flex;
  justify-content: space-between;
}

.left {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 1;
  gap: var(--spacing-150);

  min-width: 0;
  padding-block: var(--spacing-150);
}

.bottom-left {
  display: flex;
  gap: var(--spacing-050);
  justify-content: flex-start;
}

.right {
  display: flex;
  flex-shrink: 0;
  gap: var(--spacing-200);
  align-items: flex-start;

  padding: var(--spacing-150) 0;
}

.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 64px;
  padding-inline: var(--spacing-150);

  font: var(--desktop-text-xs-medium);
  color: var(--text-tertiary);

  box-shadow: 0 0 0 1px var(--border-primary);
}

.lines-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);
}

.additional-buttons {
  position: relative;
  display: flex;
  align-items: center;
  padding-left: var(--spacing-100);

  &::before {
    content: '';

    position: absolute;
    left: 0;

    width: 0.5px;
    height: 56px;

    background-color: var(--border-primary);
  }
}
</style>
